import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PenLine } from 'lucide-react'
import { TaskStartIcon, DurationIcon, TaskDeadlineIcon, RecurringIcon } from '@/src/renderer/components/icons'
import type { DetailsSection, DetailsSectionId, DetailsSectionGroup } from './useDetailsSections'
import { DETAILS_GROUPS } from './useDetailsSections'
import { railBumps, type Bump } from './detailsShape'
import './DetailsRail.scss'

/**
 * **Die Zeichen der Leiste** (Figma „2-Master", `5279:208009`; festgelegt
 * 2026-09-19, Aufmerksamkeit nachgetragen). Wer `null` trägt, steht nicht drin.
 *
 * - **Titel — der Stift** (`PenLine`, im Entwurf `5362:160821`). Bis zum
 *   19.09.2026 trug er keins, weil er ohnehin ganz oben steht. Als **Griff**,
 *   der den Caret holt, verdient er eins.
 * - **Die Notiz trägt keins.** Sie steht direkt unter dem Titel und ist
 *   ohne Sprung da; ein Ziel darauf zeigte auf etwas, das ohnehin im Bild ist.
 * - **Unteraufgaben, Aufmerksamkeit, Tags, Belohnung tragen seit 06.10.2026 keins**
 *   (Sven): Die obere Pille ist nur noch der Stift, er steht für den ganzen Kopf.
 */
const ICONS: Record<DetailsSectionId, React.ReactNode> = {
  title: <PenLine size={16} />,
  notes: null,
  subtasks: null,
  attention: null,
  tags: null,
  reward: null,
  // Ohne Zeichen: Die Erinnerung steht als Chip im Kopf, die Pillen behalten ihre Länge (Entscheidung 12).
  reminders: null,
  start: <TaskStartIcon size={16} />,
  deadline: <TaskDeadlineIcon size={16} />,
  duration: <DurationIcon size={16} />,
  recurrence: <RecurringIcon size={16} />,
}

interface DetailsRailProps {
  sections: DetailsSection[]
  /** Der Bereich, der gerade im Bild steht — aus `useSectionSpy`. */
  activeId: DetailsSectionId | null
  onJump: (id: DetailsSectionId) => void
}

interface RailGroup {
  group: Exclude<DetailsSectionGroup, null>
  sections: DetailsSection[]
  bump: Bump
}

/**
 * Die Bereiche nach Pillen sortiert, samt Lage. Gruppen ohne Bereiche fallen
 * weg — bei mehreren ausgewählten Aufgaben ist das Gruppe 1.
 */
export function groupRailSections(sections: readonly DetailsSection[]): RailGroup[] {
  const buckets = DETAILS_GROUPS.map(group => ({
    group,
    sections: sections.filter(s => s.group === group && ICONS[s.id] !== null),
  }))
  const bumps = railBumps(buckets.map(b => b.sections.length))
  const full = buckets.filter(b => b.sections.length > 0)
  return full.map((bucket, idx) => ({ ...bucket, bump: bumps[idx] }))
}

/**
 * Die **Zeichen-Leiste** — zwei Pillen LINKS neben der Karte, als Ausbuchtung
 * ihrer Silhouette (`detailsShape.ts` zeichnet sie, hier stehen nur die Ziele
 * darin). Oben der Stift für den Kopf (seit 06.10.2026 allein, Sven; vorher
 * auch Unteraufgaben, Aufmerksamkeit, Tags, Belohnung). Unten, wann sie dran
 * ist: Start, Ende, Dauer, Wiederkehrend. Ihre Ecken, ihr Rahmen und ihr Grund
 * gehören dem Pfad; die Pillen selbst sind unsichtbare Kästen. Bei mehreren
 * ausgewählten Aufgaben gibt es keinen Titel — dann steht nur die untere Pille.
 *
 * **„Hier bist du" folgt dem Rollstand**, über beide Pillen hinweg; im ganzen
 * Kopf leuchtet der Stift. Die Leiste selbst merkt davon nichts — sie bekommt
 * nur `activeId`; wer ihn bestimmt, steht in `TaskDetails`.
 *
 * **Ein Klick auf den Stift ist ein GRIFF, kein Sprung**: Er holt den Caret in
 * den Titel. Ein Sprung wäre folgenlos, weil der Kopf ohnehin oben steht.
 *
 * Zwei Auskünfte auf **zwei Kanälen**, nicht auf einer Helligkeitsachse:
 *
 * | Auskunft | Wie |
 * |---|---|
 * | „hier bist du" | getönter Kasten hinter dem Zeichen |
 * | „hier steht etwas" | ein Punkt oben rechts am Zeichen |
 *
 * Blass gegen kräftig ist in einem 32px-Ziel schwer zu treffen; ein Punkt ist
 * da oder nicht. Und beide Auskünfte können gleichzeitig gelten — deshalb
 * trägt der Punkt einen Ring in Flächenfarbe, damit er auch auf dem getönten
 * Kasten ablesbar bleibt.
 *
 * Die Pillen liegen ABSOLUT auf den Zahlen aus `railBumps`, damit Kasten und
 * Silhouette dieselbe Quelle haben. Beim Wechsel von einer auf mehrere
 * Aufgaben rücken sie per CSS-Übergang nach — der Pfad folgt ihnen im selben
 * Takt, weil er sie misst.
 */
export const DetailsRail: React.FC<DetailsRailProps> = ({ sections, activeId, onJump }) => {
  const { t } = useTranslation()
  const groups = useMemo(() => groupRailSections(sections), [sections])
  // Steht man im Kopf bei etwas ohne eigenes Zeichen (Unteraufgaben, ein Chip), leuchtet der Stift — er steht für den Kopf.
  const shownActive = activeId && ICONS[activeId] == null && sections.find(s => s.id === activeId)?.group === 1 ? 'title' : activeId

  return (
    <nav className="details-rail" aria-label={t('taskDetails.rail.ariaLabel')}>
      {groups.map(({ group, sections: items, bump }) => (
        <div
          key={group}
          className="details-rail__pill"
          data-group={group}
          style={{ top: `${bump.top}px`, height: `${bump.height}px` }}
        >
          {items.map(section => {
            const active = section.id === shownActive
            const className = [
              'details-rail__item',
              section.filled && 'details-rail__item--filled',
              active && 'details-rail__item--active',
            ].filter(Boolean).join(' ')

            return (
              <button
                key={section.id}
                type="button"
                className={className}
                aria-label={t(section.labelKey)}
                aria-current={active ? 'true' : undefined}
                onClick={() => onJump(section.id)}
              >
                {ICONS[section.id]}
              </button>
            )
          })}
        </div>
      ))}
    </nav>
  )
}
